import React, { useEffect, useState } from 'react';

import {
  createBatch,
  fetchBatch,
  updateBatch,
} from '../../services/batchService';
import { extractApiError } from '../../utils/experimentalSetups';

export function BatchRunDrawer({
  open,
  onClose,
  projects,
  batchId: existingBatchId,
  onCreated,
  inlineLayout = false,
}) {
  const editingExisting = Boolean(existingBatchId);

  const [metadata, setMetadata] = useState({
    code: '',
    name: '',
    project: '',
    protocol: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setError('');
      if (!editingExisting) {
        setMetadata((prev) => ({
          ...prev,
          code: '',
          name: '',
          project: '',
          protocol: '',
        }));
      }
    }
  }, [open, editingExisting]);

  useEffect(() => {
    if (!open || !existingBatchId) return;
    let active = true;
    setLoading(true);
    const load = async () => {
      try {
        const batch = await fetchBatch(existingBatchId);
        if (!active) return;
        setMetadata({
          code: batch.code || '',
          name: batch.name || '',
          project: batch.project || '',
          protocol: batch.protocol || '',
        });
      } catch (err) {
        console.error('Failed to load batch', err);
        if (active) {
          setError(
            extractApiError(err, 'Failed to load batch details.')
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [open, existingBatchId]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      let batch;
      if (editingExisting) {
        await updateBatch(existingBatchId, {
          name: metadata.name,
          project: metadata.project,
          protocol: metadata.protocol,
        });
        batch = await fetchBatch(existingBatchId);
      } else {
        const payload = {
          code: metadata.code,
          name: metadata.name,
          project: metadata.project,
          protocol: metadata.protocol,
        };
        batch = await createBatch(payload);
      }

      const latest = await fetchBatch(batch.id);
      onCreated?.(latest);
      onClose(true);
    } catch (error) {
      console.error(error);
      setError(extractApiError(error, 'Failed to save batch.'));
    } finally {
      setSubmitting(false);
    }
  };

  const formClass = inlineLayout
    ? 'flex h-full flex-col gap-6 overflow-y-auto rounded-lg border border-slate-200 bg-white p-6 shadow-sm'
    : 'flex h-full w-full max-w-2xl flex-col gap-6 overflow-y-auto rounded-lg bg-white p-6 shadow-2xl';

  const form = (
    <form className={formClass} onSubmit={handleSubmit}>
        <h2 className="text-lg font-semibold text-slate-900">
          {editingExisting ? 'Edit batch' : 'New batch'}
        </h2>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-slate-600">Batch code</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 p-2"
              required={!editingExisting}
              disabled={editingExisting || loading}
              value={metadata.code}
              onChange={(e) =>
                setMetadata((prev) => ({ ...prev, code: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <span className="text-slate-600">Project <span className="text-rose-500">*</span></span>
            <select
              className="mt-1 w-full rounded border border-slate-300 p-2"
              value={metadata.project}
              disabled={loading}
              required
              onChange={(e) =>
                setMetadata((prev) => ({ ...prev, project: e.target.value }))
              }
            >
              <option value="">— Select project (required) —</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code || project.name}
                </option>
              ))}
            </select>
            {!metadata.project && (
              <p className="mt-1 text-xs text-amber-600">
                Project is required for upload and grouping.
              </p>
            )}
          </label>
          <label className="block">
            <span className="text-slate-600">Tag</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 p-2"
              value={metadata.protocol}
              disabled={loading}
              onChange={(e) =>
                setMetadata((prev) => ({ ...prev, protocol: e.target.value }))
              }
            />
            <p className="mt-1 text-xs text-slate-500">
              Optional information to identify or annotate this Batch. Not used for analysis or acquisition metadata.
            </p>
          </label>
          <p className="text-xs text-slate-500">
            After you save this batch, open it in the workspace to upload Size or Encapsulation files.
          </p>
          {error && <p className="text-xs text-rose-600">⚠ {error}</p>}
        </div>

        <div className="mt-auto flex justify-between text-sm">
          <button
            type="button"
            className="rounded border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50"
            onClick={() => onClose(false)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            disabled={submitting || loading || !metadata.project}
          >
            {submitting ? 'Saving…' : editingExisting ? 'Save changes' : 'Create batch'}
          </button>
        </div>
    </form>
  );

  if (inlineLayout) {
    return <div className="h-full">{form}</div>;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      {form}
    </div>
  );
}
